<script lang="ts">
  /**
   * Imports
   * ---------------------------------------------------------------------------
   */

  import CardWrapper from "./CardWrapper.svelte";
  import Button, { type ButtonProps } from "@elements/Button.svelte";
  import { stripHtml } from "@utils/string";
  import { twJoin } from "tailwind-merge";
  import CardTags from "@components/content/Card/CardTags.svelte";
  import {
    convertStatamicAssetToCDN,
    deriveBlurredBackgroundImageURLFromText,
  } from "@utils/cdn";

  /**
   * Props
   * ---------------------------------------------------------------------------
   */

  /** orientation to use for Card layout */
  export let orientation: "portrait" | "landscape" = "portrait";

  export let featuredImageSrc: string | undefined = undefined;

  /** leader text, usually the topmost text above the title */
  export let leader: string = "";

  /** main title text */
  export let title: string = "";

  /** description text */
  export let description: string = "";

  /** Button Props*/
  export let button: ButtonProps | undefined | string = undefined;

  /** Left Call to Action */
  export let leftCTA: ButtonProps | undefined | string = undefined;

  /** Right Call to Action */
  export let rightCTA: ButtonProps | undefined | string = undefined;

  /** href - if set, then the entire Card is a link to this URL */
  export let href: undefined | string = undefined;

  /** tags to display */
  export let tags: string[] = [];

  /** pass any custom class to the topmost element */
  export let { class: classProp = "" } = $$props;

  /**
   * Data / State
   * ---------------------------------------------------------------------------
   */

  let isZoomed = false;
  const isLandscape = orientation === "landscape";
  const isPortrait = orientation === "portrait";

  const finalDescription = description;

  const isLeftCtaAString = typeof leftCTA === "string";
  const isRightCtaAString = typeof rightCTA === "string";
  const isLeftCtaAnObjectWithLink =
    typeof leftCTA === "object" && !!leftCTA?.href;
  const isRightCtaAnObjectWithLink =
    typeof rightCTA === "object" && !!rightCTA?.href;
  const isAnyCtaSet =
    isLeftCtaAString ||
    isRightCtaAString ||
    isLeftCtaAnObjectWithLink ||
    isRightCtaAnObjectWithLink;

  /**
   * Functions
   * ---------------------------------------------------------------------------
   */

  function zoomIn(e: any) {
    isZoomed = true;
  }

  function zoomOut(e: any) {
    isZoomed = false;
  }

  let featuredImageBackground: string | undefined =
    convertStatamicAssetToCDN(featuredImageSrc);

  const images = [
    "https://b-cdn.wildatheart.org/files/articles/default-images/adam-azim-ed2fq0vxsca-unsplash.jpg",
    "https://b-cdn.wildatheart.org/files/articles/default-images/adam-kool-ndn00kmbj1c-unsplash.jpg",
    "https://b-cdn.wildatheart.org/files/articles/default-images/asa-rodger-b8xmtkwlrvo-unsplash.jpg",
    "https://b-cdn.wildatheart.org/files/articles/default-images/benjamin-davies-mqn-ev9rnly-unsplash.jpg",
  ];

  if (!featuredImageBackground) {
    featuredImageBackground = deriveBlurredBackgroundImageURLFromText(
      title,
      images,
    );
  } else {
    featuredImageBackground = deriveBlurredBackgroundImageURLFromText(title, [
      featuredImageBackground,
    ]);
  }
</script>

<CardWrapper class={classProp} {orientation} {href}>
  {#if !!featuredImageBackground}
    <img
      src={`${featuredImageBackground}`}
      alt=""
      class="absolute top-0 left-0 w-full h-full object-cover"
    />
  {/if}
  <div
    class=" flex flex-col @lg:flex-row @lg:items-center w-full h-full relative z-10 bg-gradient-to-b from-black/20 to-black/80"
  >
    {#if !!featuredImageSrc}
      <img
        src={`${featuredImageSrc}`}
        alt=""
        class="top-0 left-0 w-full h-auto"
      />
    {/if}
    <div
      class="p-4 md:p-6 @lg:flex-auto @lg:w-3/4 @lg:flex @lg:flex-col @lg:justify-end"
    >
      <!-- leader, title -->
      <h3 class="text-white wah-heading-3 mb-1 line-clamp-1">{title}</h3>
      {#if leader}
        <h6 class="text-white opacity-50 wah-body-small">
          {leader}
        </h6>
      {/if}
      <!-- description -->
      <p
        class={twJoin(
          "line-clamp-3 @lg:line-clamp-3 wah-body-small md:wah-body-base text-white mt-2  fade-bottom",
        )}
      >
        {stripHtml(finalDescription)}
      </p>
      <!-- avatar, tags -->
    </div>
    <div class="p-4 md:p-6 w-full flex gap-4">
      {#if !!rightCTA}
        <Button
          {...{
            ...rightCTA,
            size: "tiny",
            variant: "pill",
          }}
        />
      {/if}
      <CardTags {tags} variant="darkGhost" />
    </div>
  </div>
</CardWrapper>
